/**
 * Cookie Consent Management Platform - 本地存储工具
 * 管理 cmp_anon_id / cmp_consent / cmp_policy_version
 */

// UUID v4 生成
export function generateUUIDv4(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// 本地存储 keys
const STORAGE_KEYS = {
  ANON_ID: 'cmp_anon_id',
  CONSENT: 'cmp_consent',
  POLICY_VERSION: 'cmp_policy_version',
} as const;

// 同意选项类型
export interface ConsentChoices {
  necessary: boolean;   // 始终 true
  functional: boolean;
  analytics: boolean;
  advertising: boolean;
}

// 默认同意（只有必要类）
export const DEFAULT_CONSENT: ConsentChoices = {
  necessary: true,
  functional: false,
  analytics: false,
  advertising: false,
};

// 全部接受
export const ACCEPT_ALL_CONSENT: ConsentChoices = {
  necessary: true,
  functional: true,
  analytics: true,
  advertising: true,
};

// 获取或创建匿名 ID
export function getOrCreateAnonId(): string {
  if (typeof window === 'undefined') return '';
  let anonId = localStorage.getItem(STORAGE_KEYS.ANON_ID);
  if (!anonId) {
    anonId = generateUUIDv4();
    localStorage.setItem(STORAGE_KEYS.ANON_ID, anonId);
  }
  return anonId;
}

// 获取匿名 ID（不创建）
export function getAnonId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.ANON_ID);
}

// 获取本地同意
export function getLocalConsent(): ConsentChoices | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONSENT);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // 校验字段
    if (
      typeof parsed.necessary === 'boolean' &&
      typeof parsed.functional === 'boolean' &&
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.advertising === 'boolean'
    ) {
      // 强制 necessary = true
      return { ...parsed, necessary: true };
    }
    return null;
  } catch {
    return null;
  }
}

// 保存本地同意
export function setLocalConsent(consent: ConsentChoices): void {
  if (typeof window === 'undefined') return;
  // 强制 necessary = true
  const toSave: ConsentChoices = { ...consent, necessary: true };
  localStorage.setItem(STORAGE_KEYS.CONSENT, JSON.stringify(toSave));
}

// 获取本地 policy version
export function getLocalPolicyVersion(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEYS.POLICY_VERSION);
}

// 保存本地 policy version
export function setLocalPolicyVersion(version: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEYS.POLICY_VERSION, version);
}


// 检查是否需要展示 Banner（无同意 或 版本不一致）
export function shouldShowBanner(currentPolicyVersion: string): boolean {
  const localConsent = getLocalConsent();
  const localVersion = getLocalPolicyVersion();
  if (!localConsent) return true;
  if (localVersion !== currentPolicyVersion) return true;
  return false;
}

